import React, { Component } from "react";
import cloneDeep from "lodash/cloneDeep";
import Modal from "@cloudscape-design/components/modal";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Select from "@cloudscape-design/components/select";
import Multiselect from "@cloudscape-design/components/multiselect";
import SpaceBetween from "@cloudscape-design/components/space-between";
import FormField from "@cloudscape-design/components/form-field";
import Alert from "@cloudscape-design/components/alert";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { getEmptyProviderEditValues, providerEditModalStore } from "../stores/providerEditModal";
import { providerOptionsStore, providerStore } from "../stores/provider";
import { prepareEditProviderPayload } from "./providerValidation";

const MAX_PROVIDER_LOGO_BYTES = 1024 * 1024;
const toCloudscapeOptions = (codes) =>
	Array.isArray(codes) ? codes.map((code) => ({ label: code, value: code })) : [];

class ProviderEditModal extends Component {
	constructor(props) {
		super(props);
		this.state = {
			providerLogoCdnUrl: "",
		};
	}

	normalizeProviderLogoName = (providerName) =>
		String(providerName || "")
			.toLowerCase()
			.replace(/'/g, "")
			.replace(/\s/g, "");

	componentDidMount() {
		this.loadProviderLogoCdnUrl();
	}

	getCdnUrlFromConfig = (response) => {
		const cdnUrl =
			response?.cdn ||
			response?.urls?.value?.cdn?.value ||
			response?.urls?.cdn?.value ||
			response?.value?.cdn?.value;
		return String(cdnUrl || "").replace(/\/+$/, "");
	};

	loadProviderLogoCdnUrl = async () => {
		const { Interface } = this.props;
		if (!Interface) return;

		try {
			const response = await Interface.request(
				"get",
				{
					class: "data",
					module: "personalPromotions",
					method: "getConfig",
				},
				{},
			);
			this.setState({ providerLogoCdnUrl: this.getCdnUrlFromConfig(response) });
		} catch (error) {
			this.setState({ providerLogoCdnUrl: "" });
		}
	};

	getProviderLogoUrl = (providerName) => {
		const cdnUrl = this.state.providerLogoCdnUrl;
		const normalizedProviderName = this.normalizeProviderLogoName(providerName);

		if (!cdnUrl || !normalizedProviderName) return "";

		return `${cdnUrl}/content/logotypes/providers/${normalizedProviderName}.svg`;
	};

	closeModal = () => {
		const { providerEditModal, setProviderEditModal } = this.props;
		setProviderEditModal({
			...providerEditModal,
			show: false,
			mode: "bulk",
			selectedIds: [],
			values: getEmptyProviderEditValues(),
			initialValues: null,
			storedErrors: {},
			error: { text: "", code: 0, timestamp: 0 },
		});
	};

	editProviders = async () => {
		const { providerEditModal, providerOptions, setProviderEditModal, Interface } = this.props;

		if (!providerEditModal.selectedIds || providerEditModal.selectedIds.length === 0) {
			setProviderEditModal({
				...providerEditModal,
				error: {
					text: "No providers selected for editing.",
					code: 0,
					timestamp: Date.now(),
				},
			});
			return;
		}

		if (providerEditModal.values.logoError) {
			setProviderEditModal({
				...providerEditModal,
				error: {
					text: providerEditModal.values.logoError,
					code: 0,
					timestamp: Date.now(),
				},
			});
			return;
		}

		const updates = [];
		for (const id of providerEditModal.selectedIds) {
			const validation = prepareEditProviderPayload(
				id,
				providerEditModal.values,
				providerOptions,
				providerEditModal.mode === "single" ? providerEditModal.initialValues : null,
			);

			if (!validation.payload) {
				const [field, reason] = Object.entries(validation.errors)[0];
				setProviderEditModal({
					...providerEditModal,
					error: {
						text: `Provider ID ${id}: ${field}: ${reason}`,
						code: 0,
						timestamp: Date.now(),
					},
				});
				return;
			}

			updates.push({
				id,
				...validation.payload,
				...(providerEditModal.selectedIds.length === 1 && providerEditModal.values.logoSvg ? { logoSvg: providerEditModal.values.logoSvg } : {}),
			});
		}

		setProviderEditModal({ ...providerEditModal, loaded: false });

		try {
			const isLogoReplacement = providerEditModal.selectedIds.length === 1 && providerEditModal.values.logoSvg;

			await Interface.request(
				"set",
				{
					class: "data",
					module: "providers",
					method: "edit",
				},
				{ updates },
			);

			Interface.addNoty("Success", isLogoReplacement ? "Provider was successfully edited and logo replaced" : "Providers were successfully edited");
			Interface.loadProvider();
			setProviderEditModal({
				...providerEditModal,
				show: false,
				mode: "bulk",
				loaded: true,
				selectedIds: [],
				values: getEmptyProviderEditValues(),
				initialValues: null,
				storedErrors: {},
				error: { text: "", code: 0, timestamp: 0 },
			});
		} catch (error) {
			Interface.addNoty("Error", error.text || error.toString());
			setProviderEditModal({
				...providerEditModal,
				loaded: true,
				error: {
					text: error.text || error.toString(),
					code: error.code || 500,
					timestamp: Date.now(),
				},
			});
		}
	};

	updateParam = (key, value) => {
		const { providerEditModal, setProviderEditModal } = this.props;
		const updatedState = cloneDeep(providerEditModal);
		updatedState.values[key] = value;
		updatedState.error = { text: "", code: 0, timestamp: 0 };
		setProviderEditModal(updatedState);
	};

	setLogoError = (text) => {
		const { providerEditModal, setProviderEditModal } = this.props;
		const updatedState = cloneDeep(providerEditModal);
		updatedState.values.logoSvg = null;
		updatedState.values.logoPreview = null;
		updatedState.values.logoError = text;
		updatedState.error = { text, code: 0, timestamp: Date.now() };
		setProviderEditModal(updatedState);
	};

	handleLogoFileChange = (event) => {
		const { providerEditModal, setProviderEditModal } = this.props;
		const file = event.target.files && event.target.files[0];

		if (providerEditModal.selectedIds.length !== 1) {
			this.setLogoError("Provider logo can only be updated for one provider at a time.");
			return;
		}

		if (!file) {
			this.setLogoError("Please choose an SVG logo file.");
			return;
		}

		if (file.size <= 0) {
			this.setLogoError("Provider logo SVG is empty.");
			return;
		}

		if (file.size > MAX_PROVIDER_LOGO_BYTES) {
			this.setLogoError("Provider logo must not exceed 1 MB.");
			return;
		}

		const isSvgMime = file.type === "image/svg+xml";
		const isSvgName = /\.svg$/i.test(file.name || "");
		if (!isSvgMime && !isSvgName) {
			this.setLogoError("Please upload a valid SVG file.");
			return;
		}

		const reader = new FileReader();
		reader.onload = () => {
			const logoSvg = String(reader.result || "").replace(/^data:[^;]*;base64,/, "data:image/svg+xml;base64,");
			const updatedState = cloneDeep(providerEditModal);
			updatedState.values.logoSvg = logoSvg;
			updatedState.values.logoPreview = logoSvg;
			updatedState.values.logoError = "";
			updatedState.error = { text: "", code: 0, timestamp: 0 };
			setProviderEditModal(updatedState);
		};
		reader.onerror = () => {
			this.setLogoError("Couldn't read provider logo file.");
		};
		reader.readAsDataURL(file);
	};

	render() {
		const { providerEditModal, provider, providerOptions } = this.props;
		const isSingleEdit = providerEditModal.mode === "single";
		const isSingleProviderSelected = providerEditModal.selectedIds.length === 1;
		const currentProvider = isSingleProviderSelected ? provider.data.find((item) => item.id === providerEditModal.selectedIds[0]) : null;
		const currentLogoUrl = currentProvider ? this.getProviderLogoUrl(currentProvider.name) : "";
		const integrationTypeOptions = toCloudscapeOptions(providerOptions?.integrationTypes);
		const countryOptions = toCloudscapeOptions(providerOptions?.countryCodes);
		const currencyOptions = toCloudscapeOptions(providerOptions?.currencyCodes);

		return (
			<Modal
				visible={providerEditModal.show}
				onDismiss={this.closeModal}
				header={isSingleEdit ? "Edit Provider" : "Edit Providers"}
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button variant="link" onClick={this.closeModal}>
								Cancel
							</Button>
							<Button variant="primary" onClick={this.editProviders} loading={!providerEditModal.loaded} disabled={!providerEditModal.loaded}>
								Save Changes
							</Button>
						</SpaceBetween>
					</Box>
				}>
				<SpaceBetween size="m">
					{providerEditModal.error.text && <Alert type="error" children={providerEditModal.error.text} />}
					{!isSingleEdit && <Box variant="p">Selected {providerEditModal.selectedIds.length} provider(s) will be updated. Unchanged fields keep each provider’s current value. Use Undo change to cancel a selection change. Removing all selected countries clears restrictions; Currency requires at least one selection.</Box>}
					{isSingleProviderSelected ? (
						<FormField label="Provider Logo (SVG)" description="Upload an SVG logo up to 1 MB" errorText={providerEditModal.values.logoError}>
							<div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
								{providerEditModal.values.logoPreview || currentLogoUrl ? (
									<div
										style={{
											width: 80,
											height: 48,
											border: "1px solid #e9ebed",
											borderRadius: "6px",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											background: "#fafafa",
										}}>
										<img src={providerEditModal.values.logoPreview || currentLogoUrl} alt="Provider logo preview" style={{ maxWidth: 64, maxHeight: 32 }} />
									</div>
								) : null}
								<input key={`${providerEditModal.show}-${providerEditModal.mode}-${providerEditModal.selectedIds.join(",")}`} type="file" accept="image/svg+xml" onChange={this.handleLogoFileChange} disabled={!providerEditModal.loaded} />
							</div>
						</FormField>
					) : (
						<FormField label="Provider Logo (SVG)">
							<Box color="text-body-secondary">Logo upload is available only when one provider is selected.</Box>
						</FormField>
					)}
					<FormField label="Name">
						<Input value={providerEditModal.values.name || ""} onChange={({ detail }) => this.updateParam("name", isSingleEdit ? detail.value : detail.value || null)} disabled={!providerEditModal.loaded} />
					</FormField>
					<FormField label="Path">
						<Input value={providerEditModal.values.path || ""} onChange={({ detail }) => this.updateParam("path", isSingleEdit ? detail.value : detail.value || null)} disabled={!providerEditModal.loaded} />
					</FormField>
					<FormField label="Order" description="Enter a numeric value for order">
						<Input type="number" value={providerEditModal.values.order ?? ""} onChange={({ detail }) => this.updateParam("order", detail.value || null)} disabled={!providerEditModal.loaded} />
					</FormField>
					<FormField label="Listed">
						<Select
							selectedOption={providerEditModal.values.listed}
							onChange={({ detail }) => this.updateParam("listed", detail.selectedOption)}
							options={[
								...(!isSingleEdit ? [{ value: null, label: "Keep current" }] : []),
								{ value: true, label: "True" },
								{ value: false, label: "False" },
							]}
							placeholder="Choose listed status"
							disabled={!providerEditModal.loaded}
						/>
					</FormField>
					<FormField label="Active">
						<Select
							selectedOption={providerEditModal.values.active}
							onChange={({ detail }) => this.updateParam("active", detail.selectedOption)}
							options={[
								...(!isSingleEdit ? [{ value: null, label: "Keep current" }] : []),
								{ value: true, label: "True" },
								{ value: false, label: "False" },
							]}
							placeholder="Choose active status"
							disabled={!providerEditModal.loaded}
						/>
					</FormField>
					<FormField label="Integration Type">
						<SpaceBetween size="xs">
							<Select
								selectedOption={providerEditModal.values.integrationType === null ? null : { label: providerEditModal.values.integrationType, value: providerEditModal.values.integrationType }}
								onChange={({ detail }) => this.updateParam("integrationType", detail.selectedOption.value)}
								options={integrationTypeOptions}
								placeholder={isSingleEdit ? "Select integration type" : "Keep current"}
								disabled={!providerEditModal.loaded}
							/>
							{!isSingleEdit && providerEditModal.values.integrationType !== null && <Button variant="link" onClick={() => this.updateParam("integrationType", null)} disabled={!providerEditModal.loaded}>
								Undo change
							</Button>}
						</SpaceBetween>
					</FormField>
					<FormField label="Restricted Countries">
						{providerEditModal.storedErrors.restrictedCountries && providerEditModal.values.restrictedCountries === null ? (
							<SpaceBetween size="xs">
								<Box>Stored value: {String(providerEditModal.storedErrors.restrictedCountries.raw)}</Box>
								<Box>{providerEditModal.storedErrors.restrictedCountries.text} Other fields can still be saved.</Box>
								<Button onClick={() => this.updateParam("restrictedCountries", [])} disabled={!providerEditModal.loaded}>Replace value</Button>
							</SpaceBetween>
						) : <SpaceBetween size="xs">
							<Multiselect
								filteringType="auto"
								selectedOptions={toCloudscapeOptions(providerEditModal.values.restrictedCountries)}
								onChange={({ detail }) => this.updateParam("restrictedCountries", detail.selectedOptions.map((option) => option.value))}
								options={countryOptions}
								placeholder={providerEditModal.values.restrictedCountries === null ? "Keep current" : "No restricted countries selected"}
								disabled={!providerEditModal.loaded}
							/>
							{!isSingleEdit && providerEditModal.values.restrictedCountries !== null && <Button variant="link" onClick={() => this.updateParam("restrictedCountries", null)} disabled={!providerEditModal.loaded}>
								Undo change
							</Button>}
						</SpaceBetween>}
					</FormField>
					<FormField label="Currency">
						{providerEditModal.storedErrors.currency && providerEditModal.values.currency === null ? (
							<SpaceBetween size="xs">
								<Box>Stored value: {String(providerEditModal.storedErrors.currency.raw)}</Box>
								<Box>{providerEditModal.storedErrors.currency.text} Other fields can still be saved.</Box>
								<Button onClick={() => this.updateParam("currency", [])} disabled={!providerEditModal.loaded}>Replace value</Button>
							</SpaceBetween>
						) : <SpaceBetween size="xs">
							<Multiselect
								filteringType="auto"
								selectedOptions={toCloudscapeOptions(providerEditModal.values.currency)}
								onChange={({ detail }) => this.updateParam("currency", detail.selectedOptions.map((option) => option.value))}
								options={currencyOptions}
								placeholder={providerEditModal.values.currency === null ? "Keep current" : "Select currencies"}
								disabled={!providerEditModal.loaded}
							/>
							{!isSingleEdit && providerEditModal.values.currency !== null && <Button variant="link" onClick={() => this.updateParam("currency", null)} disabled={!providerEditModal.loaded}>
								Undo change
							</Button>}
						</SpaceBetween>}
					</FormField>
					<FormField label="Fee" description="Enter a numeric value for fee">
						<Input type="number" step="0.01" value={providerEditModal.values.fee ?? ""} onChange={({ detail }) => this.updateParam("fee", detail.value || null)} disabled={!providerEditModal.loaded} />
					</FormField>
				</SpaceBetween>
			</Modal>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const providerEditModal = useRecoilValue(providerEditModalStore);
		const setProviderEditModal = useSetRecoilState(providerEditModalStore);
		const provider = useRecoilValue(providerStore);
		const providerOptions = useRecoilValue(providerOptionsStore);

		return <Component {...props} provider={provider} providerOptions={providerOptions} setProviderEditModal={setProviderEditModal} providerEditModal={providerEditModal} />;
	};
}

export default withRecoil(ProviderEditModal);
